import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import { getCity, getProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { LeadForm } from "../components/LeadForm";

const CITY_IMG = "https://images.unsplash.com/photo-1649113103373-ff47816ae721?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function CityLanding() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCity(null); setNotFound(false);
    getCity(slug).then(setCity).catch(() => setNotFound(true));
    getProducts({ featured: true, limit: 4 }).then(setFeatured).catch(() => {});
  }, [slug]);

  if (notFound) return (
    <main className="pt-40 pb-40 text-center">
      <p className="text-stone mb-6">City page not found.</p>
      <Link to="/" className="text-honey uppercase tracking-widest text-sm">Go Home</Link>
    </main>
  );
  if (!city) return <main className="pt-40 pb-40 text-center text-stone">Loading…</main>;

  const store = city.store_detail;

  return (
    <main data-testid="city-page" className="pt-20">
      {/* hero */}
      <section className="relative h-[60vh] min-h-[440px] overflow-hidden">
        <img src={CITY_IMG} alt={city.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16">
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-honey mb-4">Medical Cannabis in</div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-display font-extrabold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.88]">
            {city.name}
          </motion.h1>
        </div>
      </section>

      {/* seo blurb + store card */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-6">
            Your trusted cannabis pharmacy serving {city.name}, UT {city.zip}
          </h2>
          <p className="text-stone text-lg leading-relaxed mb-6">{city.blurb}</p>
          <p className="text-stone text-lg leading-relaxed mb-8">
            BeeHive Pharmacy makes medical cannabis simple for {city.name} patients — from getting your Utah
            medical card to reserving lab-tested flower, edibles, vaporizers and tinctures online for fast in-store pickup.
            Our pharmacist-led team offers honest guidance you won&rsquo;t find anywhere else in {city.zip}.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/menu" className="rounded-full bg-honey text-ink px-8 py-4 font-bold tracking-widest uppercase hover:bg-bone hover:scale-105 transition-all duration-300 inline-flex items-center gap-2">
              Shop {city.name} Menu <ArrowRight size={18} />
            </Link>
            <Link to="/medical-card" className="rounded-none border border-bone/20 px-8 py-4 font-medium tracking-widest uppercase hover:bg-bone hover:text-ink transition-colors">
              Get Your Card
            </Link>
          </div>
        </div>

        <div className="bg-ink2 border border-bone/10 p-8 h-fit">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-honey mb-6">Nearest Location</div>
          <h3 className="font-display font-bold text-xl mb-6">{store.name}</h3>
          <ul className="space-y-4 text-stone text-sm">
            <li className="flex gap-3"><MapPin size={18} className="text-honey shrink-0" /> {store.address}</li>
            <li className="flex gap-3"><Phone size={18} className="text-honey shrink-0" /> {store.phone}</li>
            <li className="flex gap-3"><Clock size={18} className="text-honey shrink-0" /> {store.hours}</li>
          </ul>
          <iframe
            title="map"
            className="w-full h-48 mt-6 grayscale contrast-125 border border-bone/10"
            loading="lazy"
            src={`https://maps.google.com/maps?q=${store.lat},${store.lng}&z=13&output=embed`}
          />
        </div>
      </section>

      {/* popular in city */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-20">
        <h2 className="font-display font-bold text-3xl tracking-tight mb-10">Popular in {city.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* lead */}
      <section className="bg-ink2 border-t border-bone/10 py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tighter leading-[0.9] mb-4">
            New to Cannabis in {city.name}?
          </h2>
          <p className="text-stone text-lg mb-10">Request a consultation and our team will walk you through getting your card and your first order.</p>
          <LeadForm kind="medical_card" cta={`Request Consultation`} defaultCity={city.name} />
        </div>
      </section>
    </main>
  );
}
